import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { fadeUp, viewport } from "@/lib/motion";
import { SERVICE_KEYS } from "@/data";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", service: "", message: "", website: "" };

export const Contact = ({ defaultService = "" }) => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ ...empty, service: defaultService });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error(c.validationError);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      setForm({ ...empty, service: defaultService });
      toast.success(c.successToast);
    } catch (err) {
      toast.error(c.errorToast);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-[#F7F6F2]/25 py-3 text-[#F7F6F2] placeholder-[#F7F6F2]/40 outline-none transition-colors duration-300 focus:border-[#C4A484]";

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0D2A27] grain overflow-hidden scroll-mt-20">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="overline text-[#C4A484] mb-6">{c.overline}</motion.p>
          <motion.h2 variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewport} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-[#F7F6F2]">{c.heading}</motion.h2>
          <motion.p variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={viewport} className="mt-6 text-[#F7F6F2]/70 leading-relaxed max-w-md">{c.para}</motion.p>
          <motion.div variants={fadeUp} custom={3} initial="hidden" whileInView="show" viewport={viewport} className="mt-10 space-y-2 text-[#F7F6F2]/70 text-sm">
            <p>{c.email}</p>
            <p>{c.serving}</p>
          </motion.div>
        </div>

        <motion.div variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="border border-[#F7F6F2]/20 p-10 h-full flex flex-col justify-center">
              <p className="font-serif text-4xl text-[#F7F6F2]">{c.successTitle}</p>
              <p className="mt-4 text-[#F7F6F2]/70">{c.successText}</p>
              <button onClick={() => setDone(false)} data-testid="contact-reset" className="mt-8 self-start rounded-full border border-[#C4A484] px-6 py-3 text-sm font-semibold text-[#C4A484] transition-colors duration-300 hover:bg-[#C4A484] hover:text-[#0D2A27]">{c.another}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-8" data-testid="contact-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={update("website")} className="absolute left-[-9999px] top-0 h-px w-px opacity-0" />
              <div className="sm:col-span-1">
                <label className="overline text-[#F7F6F2]/50">{c.nameL}</label>
                <input data-testid="contact-name" value={form.name} onChange={update("name")} className={field} placeholder={c.namePlaceholder} />
              </div>
              <div className="sm:col-span-1">
                <label className="overline text-[#F7F6F2]/50">{c.emailL}</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className={field} placeholder="jane@email.com" />
              </div>
              <div className="sm:col-span-1">
                <label className="overline text-[#F7F6F2]/50">{c.phoneL}</label>
                <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className={field} placeholder="+1 (555) 000-0000" />
              </div>
              <div className="sm:col-span-1">
                <label className="overline text-[#F7F6F2]/50">{c.treatmentL}</label>
                <select data-testid="contact-service" value={form.service} onChange={update("service")} className={`${field} appearance-none`}>
                  <option value="" className="text-[#0D2A27]">{c.selectPlaceholder}</option>
                  {SERVICE_KEYS.map((key) => (
                    <option key={key} value={t.treatments[key].name} className="text-[#0D2A27]">{t.treatments[key].name}</option>
                  ))}
                  <option value={c.notSure} className="text-[#0D2A27]">{c.notSure}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-[#F7F6F2]/50">{c.messageL}</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} className={`${field} resize-none`} placeholder={c.messagePlaceholder} />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#C4A484] px-8 py-4 text-sm font-semibold text-[#0D2A27] transition-all duration-300 hover:bg-[#F7F6F2] disabled:opacity-60">
                  {loading ? c.sending : c.submit}
                  {!loading && <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
